import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight, GraduationCap, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Department, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import {
    colleges as imColleges,
    getImSpecialtiesForInstitutionAndDiscipline,
    type ImDiscipline,
    type ImInstitution,
} from "../data/internalMissionsData";
import StepHeader from "../StepHeader";

/** Per-institution Arabic naming overrides for specialties whose shared
 *  collegeKey label differs from what a given institution publishes. Indexed by
 *  institution (departmentKey) → collegeKey → translation key. Keeps each
 *  institution's wording isolated without touching the shared college labels. */
const INSTITUTION_COLLEGE_NAME_OVERRIDES: Record<string, Record<string, string>> = {
    american_university_kuwait: {
        management_info_systems: "dream_university_college_management_info_systems_auk",
        media: "dream_university_college_media_auk",
        systems_engineering: "dream_university_college_systems_engineering_auk",
        computer_engineering: "dream_university_college_computer_engineering_auk",
        computer_science: "dream_university_college_computer_science_auk",
        international_relations: "dream_university_college_international_relations_auk",
        graphic_design: "dream_university_college_graphic_design_auk",
        social_behavioral_sciences: "dream_university_college_social_behavioral_sciences_auk",
        human_resources: "dream_university_college_human_resources_auk",
    },
    gulf_university: {
        computer_engineering: "dream_university_college_computer_engineering_gust",
        computer_science: "dream_university_college_computer_science_gust",
        english_language: "dream_university_college_english_language_gust",
        management_info_systems: "dream_university_college_management_info_systems_gust",
        business_admin: "dream_university_college_business_admin_gust",
    },
    american_international_university: {
        biomedical_engineering: "dream_university_college_biomedical_engineering_aiu",
        computer_science: "dream_university_college_computer_science_aiu",
        marketing: "dream_university_college_marketing_aiu",
        graphic_design: "dream_university_college_graphic_design_aiu",
    },
    australian_university: {
        management_info_systems: "dream_university_college_management_info_systems_au",
    },
    american_university_middle_east: {
        computer_engineering: "dream_university_college_computer_engineering_aum",
        management_info_systems: "dream_university_college_management_info_systems_aum",
        human_resources: "dream_university_college_human_resources_aum",
    },
    kuwait_college_science_technology: {
        computer_engineering: "dream_university_college_computer_engineering_kcst",
        computer_science: "dream_university_college_computer_science_kcst",
    },
    kuwait_international_law_school: {
        law: "dream_university_college_law_kilaw",
    },
};

interface Props {
    universityNameKey: string;
    institution: ImInstitution;
    discipline: ImDiscipline;
    track: Track;
    onBack: () => void;
    onSelectSpecialty: (department: Department) => void;
}

/**
 * Internal Missions — leaf specialty picker (IM-only step).
 *
 * After Track → Institution → Discipline, this lists the actual specialties.
 * Each tile shows last year's lowest accepted value stored in
 * `equivalentRate` (an equivalent rate for engineering rows; the direct HS
 * cutoff for non-engineering rows). Guidance only — nothing gates selection.
 */
export default function ChooseImSpecialty({
    universityNameKey,
    institution,
    discipline,
    track,
    onBack,
    onSelectSpecialty,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [searchQuery, setSearchQuery] = useState("");

    const specialties = getImSpecialtiesForInstitutionAndDiscipline(
        institution.key,
        discipline.key,
        track,
    );

    const resolveNameKey = (collegeKey: string): string => {
        const override = INSTITUTION_COLLEGE_NAME_OVERRIDES[institution.key]?.[collegeKey];
        if (override) {
            return override;
        }
        const c = imColleges.find((col) => col.key === collegeKey);
        return c ? getCollegeNameKey(c) : `dream_university_college_${collegeKey}`;
    };

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return specialties;
        return specialties.filter((dept) =>
            t(resolveNameKey(dept.collegeKey)).toLowerCase().includes(q),
        );
    }, [specialties, searchQuery, t]);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_choose_specialty")}
                subtitle={`${discipline.emoji} ${t(`dream_university_im_discipline_${discipline.key}`)}`}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    🏠
                    {t(universityNameKey)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white ring-1 ring-gray-200 overflow-hidden text-sm leading-none">
                        {institution.emoji}
                    </span>
                    {t(`dream_university_institution_${institution.key}`)}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ${
                    track === "scientific"
                        ? "bg-blue-50 text-blue-700 ring-blue-200"
                        : "bg-purple-50 text-purple-700 ring-purple-200"
                }`}>
                    {t(`dream_university_track_${track}`)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-theme-50 text-theme-700 ring-1 ring-theme-200">
                    <span>{discipline.emoji}</span>
                    {t(`dream_university_im_discipline_${discipline.key}`)}
                </span>
            </div>

            {specialties.length > 4 && (
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("dream_university_search_specialty")}
                        className="w-full ps-9 pe-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent placeholder:text-gray-400"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filtered.map((dept) => (
                    <button
                        key={dept.code}
                        onClick={() => onSelectSpecialty(dept)}
                        className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                    >
                        <div className="w-9 h-9 shrink-0 bg-theme-100 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-4.5 h-4.5 text-theme-700" />
                        </div>
                        <div className="flex flex-col gap-0.5 flex-1">
                            <span className="text-sm font-medium text-gray-800">
                                {t(resolveNameKey(dept.collegeKey))}
                            </span>
                            {dept.equivalentRate > 0 && (
                                <span className="text-xs text-gray-500">
                                    {t(
                                        dept.type === "equivalent"
                                            ? "dream_university_im_eng_last_year_rate_card"
                                            : "dream_university_im_noneng_last_year_hs_card",
                                        { rate: dept.equivalentRate },
                                    )}
                                </span>
                            )}
                        </div>
                    </button>
                ))}
            </div>

            {specialties.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_im_no_specialties_in_discipline")}
                </p>
            )}
        </div>
    );
}
